import { useRef, useEffect } from "react";
import { X, Plus, Bell, BellOff } from "lucide-react";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";

export type SortOption =
  | 'age_newest'
  | 'age_oldest'
  | 'rate_highest'
  | 'rate_lowest'
  | 'rate_per_mile_highest'
  | 'pickup_soonest'
  | 'pickup_latest'
  | 'trip_shortest'
  | 'trip_longest'
  | 'dh_o_shortest'
  | 'dh_o_longest'
  | 'dh_d_shortest'
  | 'dh_d_longest'
  | 'weight_heaviest'
  | 'weight_lightest'
  | 'length_longest'
  | 'length_shortest'
  | 'origin_az'
  | 'origin_za'
  | 'dest_az'
  | 'dest_za'
  | 'capacity_highest'
  | 'capacity_lowest'
  | 'picks_most'
  | 'picks_fewest'
  | 'eq_az'
  | 'eq_za'
  | 'market_az'
  | 'market_za'
  | 'company_az'
  | 'company_za';

export interface SearchTabFilters {
  origin: string;
  destination: string;
  dhoRadius: string;
  dhdRadius: string;
  equipmentTypes: string[];
  loadType: "full" | "partial" | "other" | "both";
  /** LR Direct only: load nature filter */
  loadNature?: "all" | "spot" | "dedicated" | "contract";
  minLength: string;
  minWeight: string;
  pickupDate: string | null;
  hideCanada: boolean;
  searchBack: string;
  sortBy: SortOption;
}

export interface SearchTab {
  id: string;
  label: string;
  filters: SearchTabFilters;
  hasSearched: boolean;
  alertEnabled?: boolean;
}

export const DEFAULT_FILTERS: SearchTabFilters = {
  origin: "",
  destination: "",
  dhoRadius: "150",
  dhdRadius: "150",
  equipmentTypes: [],
  // Show all load types by default (FTL, LTL, Partial, contract). A "full"
  // default silently hid non-FTL loads with no visible cue.
  loadType: "both",
  loadNature: "all",
  minLength: "",
  minWeight: "",
  pickupDate: null,
  hideCanada: false,
  searchBack: "7",
  sortBy: "age_newest",
};

export function createBlankTab(): SearchTab {
  return {
    id: crypto.randomUUID(),
    label: "New Search",
    filters: { ...DEFAULT_FILTERS, equipmentTypes: [] },
    hasSearched: false,
  };
}

interface ActiveSearchTabsProps {
  tabs: SearchTab[];
  activeTabId: string;
  onSelectTab: (tabId: string) => void;
  onAddTab: () => void;
  onCloseTab: (tabId: string) => void;
  onToggleAlert?: (tabId: string) => void;
  unseenCounts?: Record<string, number>;
}

export function ActiveSearchTabs({
  tabs,
  activeTabId,
  onSelectTab,
  onAddTab,
  onCloseTab,
  onToggleAlert,
  unseenCounts,
}: ActiveSearchTabsProps) {
  const activeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    activeRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
  }, [activeTabId]);

  const plusButton = (
    <button
      onClick={onAddTab}
      className="h-7 w-7 flex items-center justify-center rounded-md border border-dashed border-brand-teal/50 text-brand-teal hover:bg-brand-teal/10 hover:border-brand-teal transition-colors shrink-0"
      title="New search tab"
    >
      <Plus className="h-4 w-4" />
    </button>
  );

  if (tabs.length === 0) {
    return <div className="flex items-center gap-1.5 w-full py-1">{plusButton}</div>;
  }

  return (
    <ScrollArea className="w-full">
      <div className="flex items-center gap-1.5 py-1 px-0.5">
        {tabs.map((tab) => {
          const isActive = tab.id === activeTabId;
          const alertOn = !!tab.alertEnabled;
          const unseen = !isActive ? (unseenCounts?.[tab.id] ?? 0) : 0;
          return (
            <button
              key={tab.id}
              ref={isActive ? activeRef : undefined}
              onClick={() => onSelectTab(tab.id)}
              className={cn(
                "relative flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-colors shrink-0 overflow-visible",
                "border",
                isActive
                  ? "bg-brand-teal text-white border-brand-teal shadow-sm"
                  : alertOn
                    ? "bg-card text-muted-foreground border-brand-teal/40 hover:bg-accent hover:text-foreground"
                    : "bg-card text-muted-foreground border-border hover:bg-accent hover:text-foreground",
                // Bottom accent line for alert-enabled tabs
                alertOn && "after:absolute after:bottom-0 after:left-1 after:right-1 after:h-0.5 after:rounded-full",
                alertOn && isActive && "after:bg-white/60",
                alertOn && !isActive && "after:bg-brand-teal/60"
              )}
            >
              {/* Notification dot for alert-enabled tabs */}
              {alertOn && (
                <span
                  className={cn(
                    "absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full",
                    isActive ? "bg-white" : "bg-brand-teal"
                  )}
                />
              )}
              <span className="max-w-[200px] truncate">{tab.label}</span>
              {/* Unseen-count badge — never on the active tab */}
              {unseen > 0 && (
                <span
                  className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-brand-teal text-white text-[10px] font-semibold leading-none"
                  title={`${unseen} new`}
                >
                  {unseen > 99 ? '99+' : unseen}
                </span>
              )}
              {/* Alert bell toggle — only show on searched tabs */}
              {tab.hasSearched && onToggleAlert && (
                <span
                  role="button"
                  title={alertOn ? "Disable alerts" : "Enable alerts"}
                  className={cn(
                    "shrink-0 cursor-pointer transition-colors",
                    alertOn
                      ? isActive
                        ? "text-yellow-200 hover:text-yellow-100"
                        : "text-yellow-500 hover:text-yellow-600"
                      : isActive
                        ? "text-white/50 hover:text-white/80"
                        : "text-muted-foreground/40 hover:text-muted-foreground/70"
                  )}
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleAlert(tab.id);
                  }}
                >
                  {alertOn ? <Bell className="h-3 w-3" /> : <BellOff className="h-3 w-3" />}
                </span>
              )}
              <X
                className={cn(
                  "h-3 w-3 shrink-0 cursor-pointer",
                  isActive
                    ? "text-white/70 hover:text-white"
                    : "text-muted-foreground/50 hover:text-muted-foreground"
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  onCloseTab(tab.id);
                }}
              />
            </button>
          );
        })}
        {plusButton}
      </div>
      <ScrollBar orientation="horizontal" />
    </ScrollArea>
  );
}
